// © Microsoft Corporation. All rights reserved.

import {
    DesignSystemDefaults,
    neutralLayerCardContainer,
    neutralLayerFloating,
    neutralLayerL4
} from "@microsoft/fast-components-styles-msft";

/**
 * Hex string for black color
 * To be used in color recipes to define light/dark mode
 */
export const blackHexColor = "#000000";

/**
 * Hex string for white color
 * To be used in color recipes to define light/dark mode
 */
export const whiteHexColor = "#FFFFFF";

/**
 * Dark mode color for a canvas
 */
export const DarkModeBackgroundColor: string = neutralLayerL4(() => blackHexColor)(DesignSystemDefaults);

/**
 * Dark mode color for Flyout elements
 */
export const FlyoutDarkModeBackgroundColor: string = neutralLayerFloating(() => blackHexColor)(DesignSystemDefaults);

/**
 * Pallette offset color content cards with image backgrounds and a gradient overlay;
 * this number gives a white text color value over the gradient;
 * if background color starts to matter, we need to rethink how this is specified and applied
 * The higher the number, the darker the background; over 50 and we start to see white text instead of black,
 * but we don't want to go all the way to black, or the contrast rules will only give us off-white text which
 * doesn't show up very well over the gradient we use over images since they are more gray than black near the top
 */
export const ImageBackgroundColorOffset = 80;

/**
 * Light mode color for a canvas
 */
export const LightModeBackgroundColor: string = neutralLayerCardContainer(() => whiteHexColor)(DesignSystemDefaults);

/**
 * Helper function to determine light/dark mode for the browser.
 * @returns Undefined if matchMedia API is not available on the window object. True if browser is in dark mode, false otherwise.
 */
export function isDarkMode(): boolean {
    if (!window.matchMedia) {
        return undefined;
    }

    const media = window.matchMedia("(prefers-color-scheme: dark)");
    if (!media) {
        return undefined;
    }

    if (media.matches) {
        return true;
    }

    return false;
}